/** Waits before each retry of a failed run: after the first failure, the second, and so on. */
export const RUN_RETRY_DELAYS_MS: readonly number[] = [1, 2, 5, 10, 15, 30].map(
  (minutes) => minutes * 60_000,
);

/** The original run plus one attempt per scheduled delay. */
export const RUN_RETRY_MAX_ATTEMPTS = RUN_RETRY_DELAYS_MS.length + 1;

/**
 * How long after the chain's first failure the last retry may still start. The waits alone add
 * up to 63 minutes; the rest is slack for attempts that take minutes to fail, so a slow
 * provider eats into the budget instead of stretching it.
 */
export const RUN_RETRY_BUDGET_MS = 70 * 60_000;

/**
 * Whether this much time since the chain's first failure is past the budget. The schedule asks
 * it of the moment a retry would start (`decideRunRetry`); the job asks it again of the moment the
 * retry actually runs, because a worker that was away or overloaded starts a due job late. A
 * retry that starts exactly at the budget is still inside it.
 */
export function exceedsRunRetryBudget(sinceFirstFailureMs: number): boolean {
  return sinceFirstFailureMs > RUN_RETRY_BUDGET_MS;
}

/**
 * How long past its due time a scheduled retry may take to start before a client stops waiting
 * for it and shows the failure. The job runs when it is due; this covers a busy worker, not an
 * outage. A retry that does start is shown as work, however long that takes.
 */
export const RUN_RETRY_START_GRACE_MS = 3 * 60_000;

/**
 * "transient": the provider said to try again (rate limit, overload, timeout).
 * "unknown": the run died for a reason nobody could name; worth trying again, not known to clear.
 * "permanent": credentials, credit, model id, request shape, size or a block; trying again repeats it.
 */
export type RunFailureClass = "transient" | "unknown" | "permanent";

/**
 * "peer" is work another bot asked for (a `bot_message` wake). Its answer goes back to that bot,
 * which is told of a failure only once no retry is coming, so it does not redo work an attempt
 * still in flight will answer.
 */
export type RunRetryScope = "routine" | "chat" | "peer";

/** Scheduled runs, runs started in the chat and work another bot asked for retry on their own. */
export const RUN_RETRY_ENABLED_SCOPES: readonly RunRetryScope[] = ["routine", "chat", "peer"];

/**
 * The scope a run retries under, or null when its trigger is never retried. Only a run the
 * person typed into the chat counts as chat: a follow-up may be the system's own steering
 * continuation, and channel, voice, webhook and reaction runs reply somewhere a retry cannot
 * follow. A delegation from another bot answers that bot, which a retry can do as well.
 */
export function runRetryScope(trigger: string): RunRetryScope | null {
  if (trigger === "routine") return "routine";
  if (trigger === "user") return "chat";
  if (trigger === "bot_message") return "peer";
  return null;
}

/** How a recorded effect ended, as far as anyone can tell. */
export type RunEffectOutcome = "completed" | "declined" | "failed" | "unsettled";

const SETTLED_EFFECT_STATUSES = new Map<string, RunEffectOutcome>([
  ["completed", "completed"],
  ["media_completed", "completed"],
  ["denied", "declined"],
  ["media_failed", "failed"],
]);

/**
 * Only these statuses say the action's fate is known. Every other one, an unknown status
 * included, may mean the action ran, so the caller must treat it as having possibly happened.
 */
export function runEffectOutcome(status: string): RunEffectOutcome {
  return SETTLED_EFFECT_STATUSES.get(status) ?? "unsettled";
}

/** What a failed run, and any earlier attempt of its retry chain, left behind. */
export interface RunWork {
  toolCalls: number;
  /** Bot messages the run wrote into the thread. */
  botMessages: number;
  effects: readonly { status: string }[];
}

/**
 * "none": nothing happened, so a retry starts over.
 * "partial": something happened and every effect is settled, so a retry may continue.
 * "uncertain": an effect may or may not have happened; running again could repeat it.
 */
export type RunWorkState = "none" | "partial" | "uncertain";

export function runWorkState(work: RunWork): RunWorkState {
  if (work.effects.some((effect) => runEffectOutcome(effect.status) === "unsettled")) {
    return "uncertain";
  }
  return work.toolCalls > 0 || work.botMessages > 0 || work.effects.length > 0 ? "partial" : "none";
}

export interface RunRetryInput {
  failure: RunFailureClass;
  scope: RunRetryScope;
  /** Runs in the retry chain so far, the one that just failed included. */
  attempt: number;
  /** Time since the first run of the chain failed; 0 when the failed run is the first. */
  elapsedMs: number;
  /** Work across the whole chain. Leave out when none is known. */
  work?: RunWork;
}

export type RunRetryDecision =
  | {
      retry: true;
      delayMs: number;
      /** "continue" tells the new run what is already done; "restart" runs the task afresh. */
      mode: "restart" | "continue";
    }
  | {
      retry: false;
      reason:
        | "scope_disabled"
        | "permanent"
        | "work_uncertain"
        | "attempts_exhausted"
        | "budget_exhausted";
    };

const NO_WORK: RunWork = { toolCalls: 0, botMessages: 0, effects: [] };

export function decideRunRetry(
  input: RunRetryInput,
  enabledScopes: readonly RunRetryScope[] = RUN_RETRY_ENABLED_SCOPES,
): RunRetryDecision {
  if (!enabledScopes.includes(input.scope)) return { retry: false, reason: "scope_disabled" };
  if (input.failure === "permanent") return { retry: false, reason: "permanent" };
  const work = runWorkState(input.work ?? NO_WORK);
  if (work === "uncertain") return { retry: false, reason: "work_uncertain" };
  const delayMs = RUN_RETRY_DELAYS_MS[input.attempt - 1];
  if (delayMs === undefined) return { retry: false, reason: "attempts_exhausted" };
  if (exceedsRunRetryBudget(Math.max(0, input.elapsedMs) + delayMs)) {
    return { retry: false, reason: "budget_exhausted" };
  }
  return { retry: true, delayMs, mode: work === "partial" ? "continue" : "restart" };
}

/** The part of a thread snapshot that decides whether a failed run is about to retry. */
export interface RunRetrySnapshot {
  run?: {
    id: string;
    botId?: string | null;
    status: string;
    retryAt?: string | null;
  } | null;
  activeRuns?: readonly { id: string; botId?: string | null }[];
}

/** When the server planned to retry the newest run, if it failed and a retry is still expected. */
function plannedRetryTime(snapshot: RunRetrySnapshot | null): number | null {
  const run = snapshot?.run;
  if (run?.status !== "failed" || !run.retryAt) return null;
  const due = Date.parse(run.retryAt);
  return Number.isNaN(due) ? null : due;
}

/**
 * Whether the newest run failed but the system will mend it: "waiting" until its retry is due and
 * has had a moment to start, "retrying" while the retry itself works, otherwise "none". A failure
 * the server did not plan to retry, a stopped run and a retry that never started are all "none",
 * so the reader is told about them.
 */
export function runRetryState(
  snapshot: RunRetrySnapshot | null,
  now: number = Date.now(),
): "none" | "waiting" | "retrying" {
  const due = plannedRetryTime(snapshot);
  const run = snapshot?.run;
  if (due === null || !run) return "none";
  // The retry is a run of the same bot; in a group, other members' work does not count.
  const retrying = (snapshot?.activeRuns ?? []).some(
    (candidate) => candidate.botId === run.botId && candidate.id !== run.id,
  );
  if (retrying) return "retrying";
  return now < due + RUN_RETRY_START_GRACE_MS ? "waiting" : "none";
}

/**
 * The moment a hidden failure must show if its retry has not started by then, for a timer to wake
 * the page at; null when no failure is being hidden for a retry that has not started.
 */
export function runRetryDeadline(snapshot: RunRetrySnapshot | null): number | null {
  const due = plannedRetryTime(snapshot);
  if (due === null || runRetryState(snapshot, due) === "retrying") return null;
  return due + RUN_RETRY_START_GRACE_MS;
}
